/**
 * Tabbed keywords and data-input configuration panel for a SIM level.
 *
 * @module components/sim/keywords/Keywords
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
/* eslint-disable no-param-reassign */
import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {Link} from 'react-router-dom';
import Paper from '@material-ui/core/Paper';
import Toolbar from '@material-ui/core/Toolbar';
import {Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Icon, Tooltip,} from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import Button from '@material-ui/core/Button';
import Tab from '@material-ui/core/Tab';
import TabPanel from '@material-ui/lab/TabPanel';
import TabContext from '@material-ui/lab/TabContext';
import TabList from '@material-ui/lab/TabList';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import KeyboardBackspaceOutlinedIcon from '@material-ui/icons/KeyboardBackspaceOutlined';
import AppBar from '@material-ui/core/AppBar';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import PropTypes from 'prop-types';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import IconButton from '@material-ui/core/IconButton';
import {Alert} from '@material-ui/lab';
import KeywordsCheckbox from './KeywordsCheckbox.jsx';
import Data from '../../../assets/images/icons/Data.png';
import Print from '../../../assets/images/icons/Print.png';
import Details from '../Details.jsx';
import {addKeywords, decreaseStep, fetchCreateKeywords, fetchCurrentSim, reset,} from '../../../redux/sims/actions';
import {saveKeywords, uploadKeywordExcel} from '../../../services/result.service';
import LoadCase from './LoadCase';
import {setDataLoaded, sfReset} from '../../../redux/sf/actions';
import {useNavbarStyles, useTabContentStyles, useToolbarStyles,} from './Keywords.js';
// import Screening from './Screening.jsx';
// import PhysicianMap from './PhysicianMap.jsx';
// import KBCA from './KBCA.jsx';
// import Dispositions from './Dispositions.jsx';
// import KnowledgeArticle from './KnowledgeArticle.jsx';
// import Opportunity from './Opportunity.jsx';

/**
 * A functional component that provides an enhanced toolbar for interacting with a data table.
 *
 * This toolbar includes features such as toggling between "case" and "account" modes, displaying alert messages,
 * uploading data via an Excel file, navigating to a preview page, resetting specific states, and more.
 * It provides various UI elements, such as buttons, toggle groups, tooltips, and alert messages.
 *
 * @param {Object} props - The properties required by the component.
 * @param {string} props.type - The current type selected, such as "case" or "account".
 * @param {Function} props.typeChange - A callback function to handle type change events.
 * @param {Function} props.decreaseStep - A function to handle the decrease in the current step or stage.
 * @param {Function} props.sfReset - A function to handle reset actions for Salesforce-related data.
 * @param {Function} props.handleExcelUpload - A function to handle the upload of Excel files.
 * @param {boolean} props.openAlert - A flag indicating whether an alert message is currently open.
 * @param {string} props.alertSeverity - The severity level of the alert (e.g., "error", "info", etc.).
 * @param {string} props.alertMesage - The text to display in the alert message.
 * @param {Array} props.layoutData - An array containing layout data, typically including nested structures such as tabs, sections, and fields.
 * @param {Object} props.newSim - An object representing simulation data, including information such as its `id`.
 * @returns {JSX.Element} The rendered toolbar component with enhanced features for table interaction.
 */
const EnhancedTableToolbar = ({
    type,
    typeChange,
    decreaseStep,
    sfReset,
    handleExcelUpload,
    openAlert,
    alertSeverity,
    alertMesage,
    layoutData,
    newSim,
}) => {
    const classes = useToolbarStyles();
    const [showModal, setShowModal] = useState(false);
    const [currentAlignment, setCurrentAlignment] = React.useState('case');

    useEffect(() => {
        setCurrentAlignment(type);
    }, [type]);

    /**
     * Event handler function designed to handle click events.
     * Executes a series of actions in response to a user clicking an interactive element.
     *
     * Actions:
     * - Invokes the decreaseStep function to decrement a step or perform a related operation.
     * - Calls the sfReset function to reset specific states or configurations to their default state.
     *
     * This function is typically hooked to user interface elements as a callback for the "click" event.
     */
    const handleClick = () => {
        decreaseStep();
        sfReset();
    };

    /**
     * Handles processing and saving of simulation layout data to the server and updates local storage.
     *
     * This function performs the following tasks:
     * - Iterates through the `layoutData` array and removes internal `_id` properties from layouts, tabs, sections, and fields.
     * - Constructs a `requestData` object containing the current simulation ID (`sim_id`) and sanitized layout data.
     * - Sends the `requestData` to a server using the `saveKeywords` function.
     * - Updates local storage with the simulation ID and state information related to the current simulation.
     *
     * Note: The `reset` and `sfReset` methods in `props` are commented out and thus not executed.
     *
     * @async
     */
    const handleNext = async () => {
        layoutData.forEach((layout) => {
            delete layout._id;
            layout.tabs.forEach((keyword) => {
                delete keyword._id;
                keyword.sections.forEach((field) => {
                    delete field._id;
                    field.fields.forEach((element) => {
                        delete element._id;
                    });
                });
            });
        });

        const requestData = {};
        requestData.sim_id = newSim.id;
        requestData.layouts = layoutData;

        await saveKeywords(requestData);
        // props.reset();
        // props.sfReset();
        localStorage.setItem('sim_id', requestData.sim_id);
        const data = JSON.stringify({sim_id: requestData.sim_id});
        localStorage.setItem('state', data);
    };

    return (
        <Toolbar className={classes.root}>
            <Typography
                className={classes.title}
                variant="h6"
                id="tableTitle"
                component="div"
            >
                Data Input
            </Typography>
            {openAlert ? (
                <Alert
                    variant="filled"
                    severity={alertSeverity}
                    className={classes.errorAlert}
                >
                    {alertMesage}
                </Alert>
            ) : null}
            <ToggleButtonGroup
                value={currentAlignment}
                onChange={(event, newAlignment) => {
                    setCurrentAlignment(newAlignment);
                }}
                exclusive
                aria-label="text alignment"
                className={classes.toggleBtn}
            >
                <ToggleButton
                    value="case"
                    aria-label="left aligned"
                    onClick={() => {
                        typeChange('case');
                    }}
                >
                    Case
                </ToggleButton>
                <ToggleButton
                    value="account"
                    aria-label="right aligned"
                    onClick={() => {
                        typeChange('account');
                    }}
                >
                    Account
                </ToggleButton>
            </ToggleButtonGroup>
            <Tooltip
                title="Upload Excel"
                placement="top"
            >
                <IconButton
                    onClick={handleExcelUpload}
                    color="primary"
                    className={classes.icon}
                >
                    <ArrowUpwardIcon/>
                </IconButton>
            </Tooltip>
            {/* modal to load case from salesforce proxy */}
            {showModal && (
                <LoadCase showModal={showModal} setShowModal={setShowModal}/>
            )}
            {type === 'case'
                && (
                    <IconButton className={classes.icon} onClick={() => setShowModal(true)}>
                        <Icon>
                            <img src={Data} height={20} width={20} alt="data"/>
                        </Icon>
                    </IconButton>
                )}

            <IconButton className={classes.icon} style={{marginRight: '1.5rem'}}>
                <Icon>
                    <img src={Print} height={20} width={20} alt="print"/>
                </Icon>
            </IconButton>
            <Divider orientation="vertical" flexItem style={{marginTop: '5px'}}/>
            <Button
                variant="outlined"
                color="primary"
                style={{margin: '0rem 1rem', width: '43px', minWidth: '37px'}}
                disableElevation
                onClick={handleClick}
            >
                <KeyboardBackspaceOutlinedIcon/>
            </Button>
            <Link
                to={{
                    pathname: '/sims',
                }}
                target="_blank"
            >
                <Tooltip title="Save & Preview">
                    <Button
                        variant="contained"
                        color="primary"
                        disableElevation
                        size="small"
                        className={classes.saveBtn}
                        onClick={handleNext}
                    >
                        PREVIEW
                    </Button>
                </Tooltip>
            </Link>
            <Divider/>
        </Toolbar>
    );
};

/**
 * Defines the prop types for the EnhancedTableToolbar component.
 *
 * @prop {boolean} numSelected             Indicates the number of selected rows.
 * @prop {function} onRequestSort          Callback function triggered when sorting is requested.
 * @prop {string} orderBy                  Specifies the column to sort by.
 * @prop {boolean} orderDescending         Determines the sort direction; true for descending.
 * @prop {string} title                    Specifies the title of the toolbar.
 * @prop {node} children                   Optional React node(s) to render inside the toolbar.
 */
EnhancedTableToolbar.propTypes = {
    decreaseStep: PropTypes.func,
    newSim: PropTypes.object,
    sfReset: PropTypes.func,
    typeChange: PropTypes.func,
    layoutData: PropTypes.array,
    type: PropTypes.string,
    handleExcelUpload: PropTypes.func,
    openAlert: PropTypes.bool,
    alertSeverity: PropTypes.string,
    alertMesage: PropTypes.string,
};

/**
 * Navbar component used to render a tab-based navigation system. It dynamically generates tabs based
 * on the provided `keywords` and handles interactions such as tab switching and styles for the active tab.
 *
 * @param {Object} props - The properties object.
 * @param {Array} props.keywords - An array of keyword objects representing the tabs. Each keyword object
 * contains an `_id` and a `tab_name` property.
 * @param {Function} props.addKeywords - A function to add new keywords dynamically.
 * @param {Array} props.layoutData - An array representing the layout data associated with the current view.
 * @param {number} props.layoutIndex - The index of the currently selected layout.
 *
 * @returns {JSX.Element} The rendered Navbar component.
 */
const Navbar = ({
    keywords,
    addKeywords,
    layoutData,
    layoutIndex,
}) => {
    const classes = useNavbarStyles();
    const activeTabColorChange = {
        backgroundColor: 'white',
        color: 'rgb(69, 66, 158)',
        borderRadius: '3px',
    };
    const [value, setValue] = React.useState('0');
    const [tabId, setTabId] = React.useState(keywords[0]._id);

    useEffect(() => {
        setValue('0');
        setTabId(keywords[0]._id);
    }, [layoutIndex]);

    /**
     * Handles tab change events and updates the state with the selected tab value.
     *
     * @param {object} event - The event object associated with the tab change.
     * @param {any} value - The value of the selected tab.
     * @returns {void}
     */
    const handleTabs = (event, value) => {
        setValue(value);
    };

    /**
     * Updates the styling of a selected tab by setting its identifier.
     *
     * @function handleTabStyle
     * @param {Object} tab - The tab object whose style is to be handled.
     * @param {string} tab._id - The unique identifier of the tab.
     * @description This function handles tab selection by updating the internal state
     * to reflect the associated tab's identifier, allowing for dynamic styling or behavior changes.
     */
    const handleTabStyle = (tab) => {
        setTabId(tab._id);
    };

    return (
        <TabContext value={keywords.length > parseInt(value, 10) ? value : '0'}>
            <AppBar position="static" elevation={0} className={classes.navbar}>
                <TabList
                    onChange={handleTabs}
                    TabIndicatorProps={{style: {height: '0px'}}}
                >
                    {keywords.map((tab, index) => (
                        <Tab
                            key={tab._id}
                            label={tab.tab_name}
                            value={index.toString()}
                            style={tabId === tab._id ? activeTabColorChange : null}
                            className={classes.tab}
                            onClick={() => handleTabStyle(tab)}
                        />
                    ))}
                </TabList>
            </AppBar>
            {keywords.length > parseInt(value, 10)
                && (
                    <TabPanel value={value} className={classes.tabPanel}>
                        <Demographics
                            layoutData={layoutData}
                            layoutIndex={layoutIndex}
                            keywords={keywords}
                            value={value}
                            addKeywords={addKeywords}
                        />
                    </TabPanel>
                )}
        </TabContext>
    );
};

/**
 * PropTypes validation for the Navbar component.
 *
 * Defines the expected types and structure for props passed to the Navbar component.
 *
 * Properties:
 * - title: A string representing the title to be displayed in the Navbar.
 * - links: An array of objects, where each object represents a navigation link and includes the necessary attributes such as 'name' and 'url'.
 * - logo: A string representing the URL to the logo image.
 * - onLinkClick: A callback function triggered when a navigation link is clicked.
 * - theme: A string specifying the theme of the Navbar, e.g., 'light' or 'dark'.
 */
Navbar.propTypes = {
    keywords: PropTypes.array,
    addKeywords: PropTypes.func,
    layoutData: PropTypes.array,
    layoutIndex: PropTypes.number,
};

/**
 * Renders the Demographics component which facilitates dynamic data management and rendering
 * for a set of sections and fields based on provided keywords and layout data.
 *
 * @param {Object} props - The properties object passed to the component.
 * @param {Array} props.keywords - The array containing keyword data for sections and fields.
 * @param {string | number} props.value - The index or value used to determine which section's data is being processed.
 * @param {Function} props.addKeywords - Callback function to update keywords data in the parent store or state (e.g., Redux).
 * @param {Array} props.layoutData - The layout configuration data containing tab and section details.
 * @param {number} props.layoutIndex - The index of the layout within the layoutData array.
 *
 * @returns {JSX.Element} A container with dynamically rendered sections and fields. Each section contains a list of fields
 * that users can interact with via checkboxes or input fields to modify scorable values or field data.
 */
const Demographics = ({
    keywords,
    value,
    addKeywords,
    layoutData,
    layoutIndex,
}) => {
    const classes = useTabContentStyles();
    const [data, setData] = React.useState(keywords[parseInt(value, 10)].sections);
    const addDataToRedux = () => {
        layoutData[layoutIndex].tabs[parseInt(value, 10)].sections = data;
        addKeywords(layoutData);
    };

    useEffect(() => {
        setData(keywords[parseInt(value, 10)].sections);
    }, [value, keywords]);

    /**
     * Handles the change event for a checkbox within a specific field and updates its state.
     *
     * This function modifies the `is_scorable` property of a field based on the checkbox's
     * checked status. It creates a new data array, updates the corresponding field's value,
     * sets the updated data in the state, and adds the modified data to the Redux store.
     *
     * @param {Object} event - The event object from the checkbox change event.
     * @param {number} fieldTitleIndex - The index of the field title within the fields array.
     * @param {number} fieldIndex - The index of the field within the main data array.
     */
    const handleCheckboxChange = async (event, fieldTitleIndex, fieldIndex) => {
        const array = [...data];
        array[fieldIndex].fields[fieldTitleIndex].is_scorable = event.target.checked;
        setData(array);
        addDataToRedux();
    };

    /**
     * Handles the input change event for dynamically managed fields.
     *
     * Updates the value of a designated field within a nested data structure
     * based on user input. The method retrieves the input's value from the event
     * and updates the corresponding field in the data array. Once the value is updated,
     * the state is updated with the modified data.
     *
     * @param {Object} event - The event object from the input change.
     * @param {number} fieldTitleIndex - The index of the field title to be updated.
     * @param {number} fieldIndex - The index of the field within the data array to be updated.
     */
    const handleInputChange = async (event, fieldTitleIndex, fieldIndex) => {
        const array = [...data];
        const {value} = event.target;
        array[fieldIndex].fields[fieldTitleIndex].value = value;
        setData(array);
    };

    return (
        <Grid container spacing={3}>
            {data.map((keyword, fieldIndex) => (
                <Grid item xs={6} key={keyword._id}>
                    <Typography className={classes.title}>
                        <ArrowDropDownIcon className={classes.arrowDropDown}/>
                        {keyword.section_name}
                    </Typography>
                    <Grid container spacing={2}>
                        {keyword.fields.map((fieldName, fieldTitleIndex) => (
                            <Grid item xs={6} key={fieldName.field_title}>
                                <KeywordsCheckbox
                                    name={fieldName.field_title}
                                    value={fieldName.value ? fieldName.value : ''}
                                    handleCheckboxChange={handleCheckboxChange}
                                    handleInputChange={handleInputChange}
                                    fieldTitleIndex={fieldTitleIndex}
                                    fieldIndex={fieldIndex}
                                    fieldType={fieldName.type}
                                    options={fieldName.options}
                                    scorable={fieldName.is_scorable}
                                    save={addDataToRedux}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            ))}
        </Grid>
    );
};

/**
 * propTypes for the Demographics component.
 *
 * This defines the expected type and shape of the props that the
 * Demographics component can receive, ensuring proper usage and validation.
 *
 * Each key in the propTypes object corresponds to a prop the component accepts,
 * and the value defines the expected data type or structure for that prop.
 */
Demographics.propTypes = {
    keywords: PropTypes.array,
    addKeywords: PropTypes.func,
    value: PropTypes.string,
    layoutData: PropTypes.array,
    layoutIndex: PropTypes.number,
};

/**
 * Represents the props and state management for handling Simulations and Keywords functionality.
 *
 * @param fetchCurrentSim - A function to fetch the current simulation data based on an ID.
 * @param fetchCreateKeywords - A function to fetch or create keyword data.
 * @param decreaseStep - A function to decrease the current step in the process.
 * @param reset - A function to reset the state or process.
 * @param currentSim - An object representing the current simulation details.
 * @param keywords - An array of keyword data structures, which may include layouts and tabs.
 * @param sims - An object representing simulation metadata, including IDs and titles.
 * @param caseDetails - An object containing detailed attributes of a case.
 * @param addKeywords - A function to update the keyword data.
 * @param sfReset - A function to perform a specific state or component reset.
 * @param setDataLoaded - A function to update the dataLoaded flag.
 * @param dataLoaded - A boolean flag indicating whether the data has been loaded.
 */
const Keywords = ({
    fetchCurrentSim,
    fetchCreateKeywords,
    decreaseStep,
    reset,
    currentSim,
    keywords,
    sims,
    caseDetails,
    addKeywords,
    sfReset,
    setDataLoaded,
    dataLoaded,
}) => {
    const [type, setType] = React.useState('case');
    const classes = useToolbarStyles();
    const [openAlert, setOpenAlert] = React.useState(false);
    const [keywordArray, setKeywordArray] = React.useState([]);
    const [toggleIndex, setToggleIndex] = useState(0);
    const [open, setOpen] = useState(false);
    const [selectedFileName, setSelectedFileName] = useState('');
    const [selectedFile, setSelectedFile] = React.useState('');
    const [alertSeverity, setSeverity] = useState('');
    const [alertMesage, setAlertMessage] = useState('');

    useEffect(() => {
        if (sims.id && currentSim && Object.keys(currentSim).length === 0) {
            fetchCurrentSim(sims.id);
        }
    }, [fetchCurrentSim, sims.id, currentSim]);

    useEffect(() => {
        if (!keywords.length) {
            fetchCreateKeywords();
        } else if (keywords && keywords[0].tabs) {
            setKeywordArray(keywords[0].tabs ? keywords[0].tabs : []);
            setType(keywords[0].type);
        }
    }, [keywords, fetchCreateKeywords]);

    useEffect(() => {
        if (keywords.length && keywords[0].layouts) {
            keywords[0].layouts.filter((keyword, index) => {
                if (keyword.type === type) {
                    setToggleIndex(index);
                    setKeywordArray(keyword.tabs);
                }
                return true;
            });
        }
    }, [type, keywords]);

    useEffect(() => {
        if (Object.keys(caseDetails).length !== 0 && dataLoaded) {
            const data = [...keywordArray];
            for (const tab of data) {
                for (const keyword of tab.sections) {
                    for (const field of keyword.fields) {
                        if (field.attribute_name in caseDetails) {
                            if (caseDetails[field.attribute_name] !== null) {
                                field.is_scorable = !caseDetails[field.attribute_name]
                                    ? caseDetails[field.attribute_name]
                                    : true;
                            }
                            field.value = caseDetails[field.attribute_name];
                        }
                    }
                }
            }
            setKeywordArray(data);
            keywords[0].tabs = data;
            addKeywords(keywords);
            setDataLoaded(false);
        }
    }, [caseDetails, dataLoaded, addKeywords, keywordArray, setDataLoaded]);

    /**
     * Updates the current type, toggles the appropriate index, and sets the corresponding keyword array based on the specified type.
     *
     * @function typeChange
     * @param {string} type - The type to be set and used for filtering keywords.
     */
    const typeChange = (type) => {
        setType(type);

        keywords.filter((keyword, index) => {
            if (keyword.type === type) {
                setToggleIndex(index);
                setKeywordArray(keyword.tabs);
            }
        });
    };

    /**
     * Handles the Excel file upload process.
     *
     * When invoked, this function performs the necessary actions to prepare
     * the system for handling an uploaded Excel file. Typically, it sets
     * the state or triggers UI changes required for the upload process.
     *
     * In this implementation, it sets a flag or opens a modal dialog
     * representing the interface for further upload steps.
     */
    const handleExcelUpload = () => {
        setOpen(true);
    };

    /**
     * A function that handles the closure of a specific process or UI component.
     * It performs actions such as resetting the open state to false
     * and clearing any selected file name.
     */
    const handleClose = () => {
        setOpen(false);
        setSelectedFileName('');
    };

    /**
     * Handles the file selection event triggered by an input element of type 'file'.
     *
     * @param {Event} event - The file input change event containing the selected file.
     * The function extracts the file information from the event object, updates
     * the selected file name, and stores the file object for further processing.
     */
    const fileSelected = (event) => {
        setSelectedFileName(event.target.files[0].name);
        setSelectedFile(event.target.files[0]);
    };

    /**
     * Handles the file upload process and processes the given Excel file.
     *
     * This asynchronous function performs the following operations:
     * - Prepares the form data by appending the selected file.
     * - Closes the associated UI dialog by updating the state.
     * - Uploads the Excel file using the `uploadKeywordExcel` function.
     * - Based on the upload response:
     *   - If successful, triggers the keyword creation process using `fetchCreateKeywords`,
     *     displays a success alert message, and updates the severity level to 'success'.
     *   - If unsuccessful, displays an error alert message with details from the response,
     *     and updates the severity level to 'error'.
     * - Automatically closes the alert after 3 seconds.
     * - Performs additional UI cleanup by calling the `handleClose` function.
     *
     * @async
     * @function handleUpload
     * @returns {Promise<void>} A promise that resolves after the file upload process and
     *                          UI updates are completed.
     */
    const handleUpload = async () => {
        const formData = new FormData();
        formData.append('file', selectedFile);
        setOpen(false);
        await uploadKeywordExcel(formData, type, sims.id).then((res) => {
            if (res.data.success) {
                fetchCreateKeywords(sims.id);
                setOpenAlert(true);
                setSeverity('success');
                setAlertMessage('Excel Uploaded Successfully ');
            } else {
                setOpenAlert(true);
                setSeverity('error');
                setAlertMessage(res.data.message);
            }
            setTimeout(() => {
                setOpenAlert(false);
            }, 3000);
        });
        handleClose();
    };

    return (
        <Paper>
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="form-dialog-title"
            >
                <DialogTitle className={classes.uploadDialog} id="form-dialog-title">
                    Upload Excel
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Please select the file with xlsx format only
                    </DialogContentText>
                    <Button color="primary" component="label">
                        Browse
                        {' '}
                        <input
                            hidden
                            type="file"
                            accept=".xlsx, .csv"
                            onChange={fileSelected}
                        />
                    </Button>
                    {selectedFileName || (keywords && keywords.length && keywords[toggleIndex].excel_sheet_name)}
                    <Divider variant="fullWidth" className={classes.excelDivider}/>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} className={classes.cancelButton}>
                        Cancel
                    </Button>
                    <Button onClick={handleUpload} color="primary">
                        Upload
                    </Button>
                </DialogActions>
            </Dialog>
            <EnhancedTableToolbar
                decreaseStep={decreaseStep}
                reset={reset}
                newSim={sims}
                sfReset={sfReset}
                typeChange={typeChange}
                type={type}
                openAlert={openAlert}
                layoutData={keywords}
                handleExcelUpload={handleExcelUpload}
                alertSeverity={alertSeverity}
                alertMesage={alertMesage}
            />
            <Details currentSim={currentSim} title={sims.title}/>
            {keywordArray.length > 0 && (
                <Navbar
                    keywords={keywordArray}
                    layoutData={keywords}
                    layoutIndex={toggleIndex}
                    addKeywords={addKeywords}
                />
            )}
        </Paper>
    );
};

/**
 * PropTypes definition for the Keywords component.
 *
 * This defines the type and structure of props that can be passed to the
 * Keywords component, ensuring proper usage and validation.
 *
 * @typedef {Object} Keywords.propTypes
 * @property {Array<string>} [keywords] - An optional array of strings representing the keywords.
 * @property {number} maxKeywords - A number specifying the maximum number of keywords allowed.
 * @property {Function} onKeywordAdd - A callback function triggered when a new keyword is added.
 * @property {Function} onKeywordRemove - A callback function triggered when an existing keyword is removed.
 */
Keywords.propTypes = {
    fetchCurrentSim: PropTypes.func,
    fetchCreateKeywords: PropTypes.func,
    decreaseStep: PropTypes.func,
    reset: PropTypes.func,
    currentSim: PropTypes.object,
    keywords: PropTypes.array,
    sims: PropTypes.object,
    caseDetails: PropTypes.object,
    addKeywords: PropTypes.func,
    sfReset: PropTypes.func,
    setDataLoaded: PropTypes.func,
    dataLoaded: PropTypes.bool,
};

/**
 * Maps the necessary state from the Redux store to component props.
 *
 * @param {Object} state - The current Redux store state.
 * @returns {Object} An object containing the mapped props:
 * - `status` (String): The status of the simulation process from `state.sim`.
 * - `sims` (Array): The list of new simulations from `state.sim`.
 * - `keywords` (Array): The created keywords for simulation from `state.sim`.
 * - `currentSim` (Object): The currently selected simulation from `state.sim`.
 * - `caseDetails` (Object): Details of the currently selected case from `state.sf`.
 * - `dataLoaded` (Boolean): A flag indicating whether the required data has been loaded from `state.sf`.
 */
const mapStateToProps = (state) => ({
    status: state.sim.status,
    sims: state.sim.newSim,
    keywords: state.sim.createKeywords,
    currentSim: state.sim.currentSim,
    caseDetails: state.sf.caseDetails,
    dataLoaded: state.sf.dataLoaded,
});

/**
 * An object containing action dispatcher functions mapped as keys. These are used to bind
 * actions to the component dispatch functionality, enabling the component to directly call
 * these actions for state updates in a Redux environment.
 *
 * @property {Function} fetchCreateKeywords - Dispatches an action to create keywords.
 * @property {Function} fetchCurrentSim - Dispatches an action to fetch the current simulation data.
 * @property {Function} decreaseStep - Dispatches an action to decrement the step in a process or flow.
 * @property {Function} addKeywords - Dispatches an action to add new keywords to the state.
 * @property {Function} reset - Dispatches an action to reset the state or a specific part of it.
 * @property {Function} sfReset - Dispatches an action to perform a specialized reset operation.
 * @property {Function} setDataLoaded - Dispatches an action to update the state indicating that data has been loaded.
 */
const mapDispatchToProps = {
    fetchCreateKeywords,
    fetchCurrentSim,
    decreaseStep,
    addKeywords,
    reset,
    sfReset,
    setDataLoaded,
};

export default connect(mapStateToProps, mapDispatchToProps)(Keywords);
